import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { compact, flatMap, isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardGrid, CardGridItem } from 'src/components/cardGrid';
import { SearchBar } from 'src/components/input';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { PATHS } from 'src/constants/paths';
import { ComponentListCard } from 'src/features/components/modules/componentListCard/ComponentListCard';
import { ComponentPreviewSidebar } from 'src/features/components/modules/componentPreviewSidebar/ComponentPreviewSidebar';
import { DyProductSource, IComponent } from 'src/interfaces';
import { generateAssemblyProductFilters, lang } from 'src/utils';

import { Modal } from '../Modal';
import { SimpleDialog } from '../simpleDialog';
import { useFilteredComponents } from './ComponentListModal.hook';
import { ComponentListModalProps } from './ComponentListModal.interface';
import {
  Container,
  FluidWidthColumn,
  HeaderTitle,
  HeaderWrapper,
  NoComponentsWrapper,
  SearchAndCloseIconWrapper,
} from './ComponentListModal.styled';
import { ComponentListAlert } from './modules/componentListAlert/ComponentListAlert';

export const ComponentListModal = (props: ComponentListModalProps) => {
  const {
    componentsData,
    isLoadingComponents,
    errorLoadingComponents,
    getNextComponentPage,
    selectedComponent,
    inputValue,
    searchComponents,
    isComponentsDataStaled,
    setSelectedComponent,
  } = useFilteredComponents(
    props.filters,
    true,
    props?.suggestedComponentTypes,
    true,
    true,
    true,
  );

  const { productFilters, productVariationFilters } = useMemo(() => {
    const allComponents = componentsData?.pages
      ? compact(flatMap(componentsData.pages, (page) => page.data))
      : [];

    return generateAssemblyProductFilters([], allComponents);
  }, [componentsData?.pages]);

  const showEmptyComponent =
    !isComponentsDataStaled &&
    isEmpty(componentsData?.pages[0]?.data) &&
    isEmpty(inputValue) &&
    !isLoadingComponents;

  const showEmptySearchResult =
    isEmpty(componentsData?.pages[0]?.data) && !isEmpty(inputValue);

  const handleCancel = () => {
    props.onClose();
  };
  const navigate = useNavigate();

  return (
    <Modal {...props} onClose={handleCancel}>
      <Container
        $showEmptyComponent={
          showEmptyComponent && !props.disableCreatingComponentOO
        }>
        {showEmptyComponent && !props.disableCreatingComponentOO ? (
          <>
            {!props.hideCreatingComponent ? (
              <SimpleDialog
                open={true}
                cancelText={lang.cancel}
                confirmText={lang.createComponent}
                title={lang.createNewComponent}
                subTitle={lang.noComponentsMatchSectionSize}
                onClose={props.onClose}
                onClickCancel={props.onClose}
                modalSize="sm"
                onClickConfirm={() => {
                  props.onClose();
                  navigate(PATHS.COMPONENT_ADD);
                }}
              />
            ) : (
              <>
                <IconButton
                  aria-label="close"
                  onClick={props.onClose}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}>
                  <CloseIcon />
                </IconButton>
                <NoComponentsWrapper>
                  <Typography variant="h4" component="h4">
                    {lang.noComponentsMatchSectionSize}
                  </Typography>
                </NoComponentsWrapper>
              </>
            )}
          </>
        ) : (
          <>
            <HeaderWrapper>
              <HeaderTitle>
                {props.title ? props.title : 'Components'}
              </HeaderTitle>
              <SearchAndCloseIconWrapper>
                <SearchBar
                  value={inputValue}
                  isLoading={isLoadingComponents}
                  onInputChange={searchComponents}
                  placeholder={'Search'}
                />
                <IconButton aria-label="close" onClick={handleCancel}>
                  <CloseIcon />
                </IconButton>
              </SearchAndCloseIconWrapper>
            </HeaderWrapper>

            {!showEmptyComponent && (
              <ComponentListAlert
                hasDYR={
                  !!props?.suggestedComponentTypes?.includes(
                    DyProductSource.DYR,
                  )
                }
                hasDYS={
                  !!props?.suggestedComponentTypes?.includes(
                    DyProductSource.DYS,
                  )
                }
                infoMessage={props.infoMessage}
              />
            )}

            <DialogContent>
              {isLoadingComponents ? (
                <Progress />
              ) : errorLoadingComponents ? (
                <Typography>{lang.error}</Typography>
              ) : props.disableCreatingComponentOO && showEmptyComponent ? (
                <Alert severity="warning" data-testid="no-component-alert">
                  {lang.noComponentMatchType}
                </Alert>
              ) : (
                <>
                  {showEmptySearchResult ? (
                    <NoResultAlert />
                  ) : (
                    <Grid container spacing={4} mt={1}>
                      <FluidWidthColumn
                        item
                        xs={12}
                        md={selectedComponent === undefined ? 12 : 8}>
                        <CardGrid
                          isLoading={isLoadingComponents}
                          error={errorLoadingComponents}
                          updatePage={getNextComponentPage}>
                          {componentsData?.pages.map(
                            (page: { data: IComponent[] }) =>
                              page.data.map(
                                (component: IComponent, key: number) => (
                                  <CardGridItem item key={key}>
                                    <ComponentListCard
                                      component={component}
                                      selectedComponentId={
                                        selectedComponent?.id
                                      }
                                      onSelectedComponent={() =>
                                        setSelectedComponent(component)
                                      }
                                    />
                                  </CardGridItem>
                                ),
                              ),
                          )}
                        </CardGrid>
                      </FluidWidthColumn>
                      {selectedComponent && (
                        <Grid item xs={12} md={4}>
                          <ComponentPreviewSidebar
                            highlightedComponent={selectedComponent}
                            hasSidebarPreviewLoaded={true}
                            hideSidebarPreviewButton={true}
                            isLoadingSidebarPreview={false}
                            productFilters={productFilters}
                            productVariationFilters={productVariationFilters}
                            skipProductsApiOnEmptyFilters={true}
                            componentTags={[]}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                disabled={
                  !selectedComponent ||
                  (props.disableCreatingComponentOO && showEmptyComponent)
                }
                onClick={() =>
                  selectedComponent &&
                  props.onSelectedComponent(selectedComponent)
                }
                startIcon={<DashboardIcon />}>
                {props.buttonText || lang.addComponent}
              </Button>
            </DialogActions>
          </>
        )}
      </Container>
    </Modal>
  );
};
